import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {fetchTranslations} from "../types/translationsClients";
import ContactComponent from "../components/ContactComponent";
import DocklineFX from "../components/DocklineFX";
import HeroFX from "../components/HeroFX";
import { Helmet } from 'react-helmet-async';

const Systems = () => {
    const navigate = useNavigate();

    const [dict, setDict] = useState<Record<string, string> | null>(null);
    const [logos, setLogos] = useState<string[]>([]);
    const locale = "de";
    const namespace = "landing";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchTranslations({
                    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
                    locale,
                    namespace
                });
                if (!cancelled) setDict(data.items);
            } catch (e) {
                if (!cancelled) setDict({});
                console.error("Failed to load translations (landing)", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const bgStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none"
    };

    // Landing hero parallax + tilt on scroll with visibility toggle
    useEffect(() => {
        const hero = document.querySelector('.landing-hero-cover') as HTMLElement | null;
        if (!hero) return;

        const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

        let rafId = 0;
        const loop = () => {
            const doc = document.documentElement;
            const st = doc.scrollTop || document.body.scrollTop || 0;

            // Calculate halfway point of the total scrollable distance
            const maxScroll = Math.max(1, doc.scrollHeight - doc.clientHeight);
            const hideThreshold = 0.5 * maxScroll; // hide beyond 50% of page height

            if (st >= hideThreshold) {
                // Hide hero when scrolled beyond threshold
                hero.classList.add('lh-hidden');
            } else {
                // Show hero and continue parallax/tilt while above threshold
                hero.classList.remove('lh-hidden');

                const vh = window.innerHeight || 1;
                // Map the first ~140% of viewport height to 0..1 progress (slower progression)
                const t = clamp(st / (vh * 1.4));

                // Smaller effect so it lasts longer: scale 1.0 → 0.90, rotateX 0deg → 6deg
                const scale = 1 - 0.20 * t;
                const tilt = 6 * t;

                // Keep anchored while adding perspective, scaling, and tilt
                hero.style.transform = `translateX(-50%) perspective(1000px) scale(${scale}) rotateX(${tilt}deg)`;
            }

            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);
        return () => {
            hero.classList.remove('lh-hidden');
            cancelAnimationFrame(rafId);
        };
    }, []);

    return (
        <>
        <Helmet>
            <title>IT-Administration & IT-Service in München | Dockline Systems</title>
            <meta
                name="description"
                content="Dockline Systems bietet IT-Administration, IT-Support, Netzwerkadministration und Security für Unternehmen und Privatkunden in München. Zuverlässig, sicher und persönlich."
            />
            <meta
                name="keywords"
                content="IT Administrator München, IT Service München, IT Support München, Netzwerkadministrator München, IT Betreuung München, Dockline Systems"
            />
            <link rel="canonical" href="https://dockline.digital/systems" />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content="Dockline Systems – IT-Administration & Support in München" />
            <meta
                property="og:description"
                content="IT-Administration, IT-Support, Netzwerkmanagement und Security in München. Dockline Systems – Ihr zuverlässiger IT‑Partner."
            />
            <meta property="og:url" content="https://dockline.digital/systems" />
            <meta property="og:image" content="https://dockline.digital/pics/og/systems-og.jpg" />
            <meta property="og:locale" content="de_DE" />

            {/* JSON-LD schema for IT Service */}
            <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "ITService",
              "name": "Dockline Systems",
              "url": "https://dockline.digital/systems",
              "logo": "https://dockline.digital/pics/logo/sec/dsystems_logo_sec.svg",
              "serviceType": "IT-Administration & IT-Support",
              "description": "IT-Administration, Netzwerkmanagement, Security und IT-Support für Unternehmen und Privatkunden in München.",
              "areaServed": {
                "@type": "City",
                "name": "München",
                "addressCountry": "DE"
              },
              "provider": {
                "@type": "Organization",
                "name": "Dockline Digital"
              }
            }
            `}</script>
            {/* JSON-LD FAQ schema */}
            <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What does Dockline Systems actually do?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Dockline Systems provides IT administration, network management, endpoint security and ongoing IT support. We look after servers, clients, networks and user endpoints so your team can focus on their work instead of troubleshooting technology."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you only work with larger companies?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No. Dockline Systems supports small and medium-sized businesses, independent professionals and private clients. We design support models that fit your size, from a few devices up to full office environments."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you offer remote support, on-site visits, or both?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We are happy to support you both remotely and on site. Many issues can be solved quickly via secure remote access, but for hardware, cabling, Wi-Fi coverage or office moves we regularly come by in person in the Munich region. Together we choose whichever option gets you back up and running fastest."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How is IT administration and support priced?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "IT administration and support are usually billed based on the actual work time. For ongoing clients we agree on an hourly rate and, where it makes sense, fixed prices for clearly defined project work. After a short initial consultation you receive a transparent estimate so you know what to expect."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can you take over an existing IT environment from a previous provider?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. We can take over existing infrastructures, document the current setup and gradually improve it. The goal is a smooth transition without downtime, with clear documentation and stable day-to-day operations."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you also support home offices and private users?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. In addition to business clients we also help private users and home offices with topics like Wi-Fi coverage, backups, malware cleanup, device setup and general questions around secure everyday use."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How fast do you respond when something breaks?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Response times depend on the service model we agree on, but in general we aim to respond to critical issues as quickly as possible, typically on the same business day. For ongoing clients we define clear expectations so you know what to expect."
                  }
                }
              ]
            }
            `}</script>
        </Helmet>

        <div style={{'--fx-color': '#1BA27A', '--fx-glow': 'rgba(27,162,122,0.35)'} as React.CSSProperties}>
            <div className="landing-hero-cover hero-anim">
                <img src="/pics/landingPref2Systems.webp" alt="" className="landing-hero" id="landing-hero"/>
                <HeroFX/>
                <div className="landing-hero-centered-text">
                    <h1 className="systems-color">Dockline Systems</h1>
                    <h2>IT-Administration & -Support</h2>
                </div>
                <a
                    className="landing-hero-bottom-hint"
                    onClick={() => window.scrollTo({top: window.innerHeight, behavior: 'smooth'})}
                    style={{cursor: 'pointer'}}
                >
                    Scroll to Explore
                </a>
            </div>

            <div className="content">
                <DocklineFX color="#1BA27A"/>

                {/* === Systems Intro Section === */}
                <section className="systems-intro">
                    <div className="section-intro">
                        <h2 className="systems-color">What Dockline Systems Does</h2>
                        <p>Dockline Systems provides IT administration, network management, endpoint protection, and reliable IT support for businesses and private clients. We help teams stay secure, stable, and productive through clear processes and modern infrastructure.</p>
                    </div>
                </section>

                <div className="munich-local-link">
                    <Link to="/it-administrator-muenchen" className="local-link-button">
                        IT-Administrator in München →
                    </Link>
                </div>

                {/* === Service Grid === */}
                <section className="systems-grid" aria-labelledby="systems-grid-title">
                    <div className="section-intro">
                        <h2 id="systems-grid-title">Our Focus Areas</h2>
                        {/*<p>Technology built to stay out of your way — high availability and security by design.</p>*/}
                        <p>We work with all kinds of businesses — current clients include consultancies and medical practices.</p>
                    </div>
                    <div className="systems-grid__cards">
                        <article className="systems-card">
                            <i className="bi bi-hdd-network"></i>
                            <h3>Network Infrastructure</h3>
                            <p>Planning, setup, and optimization of wired and wireless networks for performance and
                                reliability.</p>
                        </article>

                        <article className="systems-card">
                            <i className="bi bi-shield-lock"></i>
                            <h3>Security & Compliance</h3>
                            <p>Endpoint protection, patch management, and GDPR-aligned configurations with auditable controls.</p>
                        </article>

                        <article className="systems-card">
                            <i className="bi bi-person-gear"></i>
                            <h3>Managed IT Support</h3>
                            <p>Monitoring, maintenance, and remote assistance with transparent SLAs and proactive
                                incident handling.</p>
                        </article>
                        <article className="systems-card">
                            <i className="bi bi-house-gear"></i>
                            <h3>Private IT Support</h3>
                            <p>Friendly help for private customers — everyday issues like Wi‑Fi, backups, malware cleanup, device setup & advice.</p>
                        </article>
                    </div>
                </section>

                {/* === Process Snapshot === */}
                <section className="systems-process" aria-labelledby="systems-process-title">
                    <div className="section-intro">
                        <h2 id="systems-process-title">How We Keep Systems Running</h2>
                        <p>Our process blends automation, expertise, and transparency — every step of the way.</p>
                    </div>
                    <ol className="process-steps">
                        <li className="process-step">
                            <div className="step-badge">1</div>
                            <h3>Audit</h3>
                            <p>We assess your existing infrastructure and identify weak spots before they cause
                                trouble.</p>
                        </li>
                        <li className="process-step">
                            <div className="step-badge">2</div>
                            <h3>Stabilize</h3>
                            <p>We patch, optimize, and automate to restore stability and performance.</p>
                        </li>
                        <li className="process-step">
                            <div className="step-badge">3</div>
                            <h3>Automate</h3>
                            <p>We set up monitoring, backups, and alerting so your systems manage themselves.</p>
                        </li>
                        <li className="process-step">
                            <div className="step-badge">4</div>
                            <h3>Support</h3>
                            <p>Continuous improvements, clear reports, and responsive help — always human, never
                                scripted.</p>
                        </li>
                    </ol>
                </section>

                {/* === Why Dockline Systems === */}
                <section className="systems-why" aria-labelledby="systems-why-title">
                    <div className="section-intro">
                        <h2 id="systems-why-title" className="systems-color">Why Dockline Systems?</h2>
                        <p>
                            Dockline Systems combines hands-on IT administration with a lean, personal setup in Munich.
                            You work directly with the person who looks after your systems – no ticket ping-pong, no
                            anonymous call center.
                        </p>
                    </div>
                    <div className="systems-why__grid">
                        <article className="systems-card systems-why__item">
                            <h3>Personal and direct</h3>
                            <p>
                                You always speak to the same person – the one who actually knows your setup. That makes
                                troubleshooting faster, planning easier and communication more relaxed than with large,
                                anonymous providers.
                            </p>
                        </article>
                        <article className="systems-card systems-why__item">
                            <h3>Local in Munich, on site when needed</h3>
                            <p>
                                Many issues can be solved quickly via secure remote access. But for topics like cabling,
                                Wi-Fi coverage, hardware or office moves, Dockline Systems comes by in person in Munich
                                and the surrounding area.
                            </p>
                        </article>
                        <article className="systems-card systems-why__item">
                            <h3>Lean structure, fair pricing</h3>
                            <p>
                                As an independent IT administrator and student with several years of practical experience,
                                I work without large-agency overhead. That means professional IT support and
                                <em> transparent, fair hourly rates</em> – especially attractive for small businesses,
                                practices and self-employed clients.
                            </p>
                        </article>
                    </div>
                </section>

                {/* === FAQ === */}
                <section className="systems-faq" aria-labelledby="systems-faq-title">
                    <div className="section-intro">
                        <h2 id="systems-faq-title" className="systems-color">Frequently Asked Questions</h2>
                        <p>
                            A few common questions about how Dockline Systems works with businesses and private clients.
                            If you do not see your situation here, just reach out and we will be happy to clarify.
                        </p>
                    </div>
                    <div className="faq-list">
                        <details className="faq-item">
                            <summary><p className="boldText">What does Dockline Systems actually do?</p></summary>
                            <p>
                                Dockline Systems provides IT administration, network management, endpoint security and ongoing
                                IT support. We look after servers, clients, networks and user endpoints so your team can focus
                                on their work instead of troubleshooting technology.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Do you only work with larger companies?</p></summary>
                            <p>
                                No. We support small and medium-sized businesses, independent professionals and private clients.
                                Support models are tailored to your size, from a handful of devices up to full office environments.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Do you offer remote support, on-site visits, or both?</p></summary>
                            <p>
                                We are happy to support you both remotely and on site. Many issues can be solved quickly via
                                secure remote access, but for hardware, cabling, Wi‑Fi coverage or office moves we regularly
                                come by in person in the Munich region.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">How is IT administration and support priced?</p></summary>
                            <p>
                                IT administration and support are usually billed based on the actual work time. For ongoing
                                clients we agree on a clear hourly rate and, where it makes sense, fixed prices for well‑defined
                                project work. After a short initial consultation you receive a transparent estimate so you know
                                what to expect.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Can you take over an existing IT environment from a previous provider?</p></summary>
                            <p>
                                Yes. We can take over existing infrastructures, document the current setup and gradually improve it.
                                The goal is a smooth transition without downtime, with clear documentation and stable day-to-day operations.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Do you also support home offices and private users?</p></summary>
                            <p>
                                Yes. In addition to business clients we also help private users and home offices with Wi‑Fi, backups,
                                malware cleanup, device setup and general questions around secure everyday use.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">How fast do you respond when something breaks?</p></summary>
                            <p>
                                Response times depend on the service model we agree on, but in general we aim to react to critical
                                issues as quickly as possible, typically on the same business day for ongoing clients.
                            </p>
                        </details>
                    </div>
                </section>

                {/* === CTA === */}
                <section className="systems-cta">
                    <h2 className="systems-color">Ready to reduce downtime and risk?</h2>
                    <p id="systems-cta-p">Available for businesses and private customers.</p>
                    <Link to="/" state={{scrollTo: 'contact-section'}}>
                        <button><p>Get in Touch</p></button>
                    </Link>
                </section>
            </div>
        </div>
    </>
    );
};
export default Systems;